import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Post } from '@/hooks/usePosts';

interface FooterProps {
  popularPosts: Post[];
}

export default function Footer({ popularPosts }: FooterProps) {
  const { settings: siteSettings } = useSiteSettings();

  const socialLinks: { key: string; icon: string; label: string; url: string }[] = [
    { key: 'instagramUrl', icon: 'ri-instagram-line', label: 'Instagram', url: siteSettings?.instagramUrl || '' },
    { key: 'facebookUrl', icon: 'ri-facebook-circle-line', label: 'Facebook', url: siteSettings?.facebookUrl || '' },
    { key: 'pinterestUrl', icon: 'ri-pinterest-line', label: 'Pinterest', url: siteSettings?.pinterestUrl || '' },
    { key: 'youtubeUrl', icon: 'ri-youtube-line', label: 'YouTube', url: siteSettings?.youtubeUrl || '' },
  ];

  const activeSocialLinks = socialLinks.filter(s => s.url);

  return (
    <footer className="w-full bg-background-100 border-t border-background-200">
      {/* Top area — Logo + Nav + Categories */}
      <div className="bg-background-100 pt-8 md:pt-12 pb-6 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {/* Logo + Desc */}
            <div className="col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-2 md:mb-4 cursor-pointer">
                <div className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center rounded-full overflow-hidden border-2 border-primary-500 flex-shrink-0">
                  {siteSettings?.logoUrl ? (
                    <img src={siteSettings.logoUrl} alt={siteSettings?.siteName || 'Miaudecasa'} className="w-full h-full object-cover" />
                  ) : (
                    <img
                      src="https://readdy.ai/api/search-image?query=Cute%20cartoon%20style%20orange%20tabby%20cat%20face%20portrait%20illustration%20on%20clean%20white%20background%2C%20friendly%20expression%2C%20round%20eyes%2C%20minimalist%20design%2C%20warm%20orange%20tones%2C%20adorable%20pet%20mascot%20design&width=120&height=120&seq=logo-miaudecasa-footer-cat-03&orientation=squarish"
                      alt="Miaudecasa"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div>
                  <div className="font-heading font-bold text-primary-500 text-sm md:text-base leading-none">{siteSettings?.siteName || 'miaudecasa'}</div>
              {siteSettings?.siteSubtitle && (
                <div className="text-foreground-500 text-[10px] md:text-xs">{siteSettings.siteSubtitle}</div>
              )}
                </div>
              </Link>
              <p className="text-foreground-500 text-[10px] md:text-xs leading-relaxed mb-3 md:mb-5 max-w-xs">
                Conteúdo de qualidade para ajudar você a proporcionar uma vida mais feliz e saudável para o seu gato.
              </p>
              {activeSocialLinks.length > 0 && (
                <div className="flex items-center gap-1.5 md:gap-3">
                  {activeSocialLinks.map(sl => (
                    <a
                      key={sl.key}
                      href={sl.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                      title={sl.label}
                    >
                      <i className={`${sl.icon} text-base md:text-lg`}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Navegação */}
            <div>
              <h4 className="font-bold text-foreground-950 text-[10px] md:text-xs tracking-widest uppercase mb-3 md:mb-4">Navegação</h4>
              <ul className="flex flex-col gap-1.5 md:gap-2.5">
                {[
                  { label: 'Início', path: '/' },
                  { label: 'Sobre nós', path: '/sobre' },
                  { label: 'Fale conosco', path: '/contato' },
                  { label: 'Privacidade', path: '/privacidade' },
                ].map(item => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-foreground-600 hover:text-primary-500 text-[10px] md:text-sm transition-colors cursor-pointer">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Categorias */}
            <div>
              <h4 className="font-bold text-foreground-950 text-[10px] md:text-xs tracking-widest uppercase mb-3 md:mb-4">Categorias</h4>
              <ul className="flex flex-col gap-1.5 md:gap-2.5">
                {[
                  { label: 'Saúde', id: 'saude' },
                  { label: 'Comportamento', id: 'comportamento' },
                  { label: 'Alimentação', id: 'alimentacao' },
                  { label: 'Raças', id: 'racas' },
                  { label: 'Curiosidades', id: 'curiosidades' },
                  { label: 'Adoção', id: 'adocao' },
                ].map(cat => (
                  <li key={cat.id}>
                    <Link to={`/categoria/${cat.id}`} className="text-foreground-600 hover:text-primary-500 text-[10px] md:text-sm transition-colors cursor-pointer">
                      {cat.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Popular — on desktop only */}
            <div className="hidden lg:block">
              <h4 className="font-bold text-foreground-950 text-[10px] md:text-xs tracking-widest uppercase mb-3 md:mb-4">Popular</h4>
              <ul className="flex flex-col gap-4">
                {popularPosts.slice(0, 3).map(post => (
                  <li key={post.id}>
                    <Link to={`/post/${post.slug}`} className="flex items-start gap-3 group cursor-pointer">
                      <div className="w-14 h-14 flex-shrink-0 rounded overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <span className="text-foreground-700 text-xs leading-snug group-hover:text-primary-500 transition-colors line-clamp-3">
                        {post.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom area — Popular (mobile) + Copyright */}
      <div className="bg-background-200 pt-6 md:pt-0 pb-4 md:pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Popular on mobile */}
          <div className="lg:hidden mb-6">
            <h4 className="font-bold text-foreground-950 text-[10px] tracking-widest uppercase mb-3">Popular</h4>
            <div className="grid grid-cols-3 gap-3">
              {popularPosts.slice(0, 3).map(post => (
                <Link key={post.id} to={`/post/${post.slug}`} className="group cursor-pointer">
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-1.5">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <span className="text-foreground-700 text-[10px] leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                    {post.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-background-300 pt-4 md:pt-6 flex items-center justify-center text-center">
            <p className="text-foreground-400 text-[10px] md:text-xs w-full">
              © 2026 miaudecasa. Todos os direitos reservados. Orgulhosamente desenvolvido por{' '}
              <a
                href="https://www.serraconsultoria.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 font-bold transition-colors cursor-pointer"
              >
                Serra Consultoria
              </a>.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
